import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import type { CaseStudy } from '../types';

const CaseStudySection = () => {
  const caseStudies: CaseStudy[] = [
  {
    id: '1',
    clientName: 'TechStart Solutions',
    industry: 'SaaS',
    challenge: 'Manual lead qualification process taking 20+ hours weekly, resulting in delayed follow-ups and lost opportunities.',
    solution: 'Implemented n8n workflow automating lead capture, enrichment, scoring, and CRM integration with intelligent routing.',
    platform: 'n8n',
    results: {
      timeSaved: '18 hours/week',
      costReduction: '$45,000/year',
      efficiency: '92% faster response'
    },
    image: "https://images.unsplash.com/photo-1531537264351-c1952d1db1f5",
    alt: 'Modern office workspace with team members collaborating around computer displaying workflow automation dashboard',
    testimonial: {
      quote: 'The automation transformed our sales process completely. We now respond to leads within minutes instead of hours, and our conversion rate has increased by 34%. The ROI was evident within the first month.',
      author: 'Sarah Mitchell',
      position: 'VP of Sales',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bdbe6c11-1763300715032.png",
      avatarAlt: 'Professional woman with short brown hair wearing navy blue blazer smiling at camera'
    }
  },
  {
    id: '2',
    clientName: 'Global Retail Co',
    industry: 'E-commerce',
    challenge: 'Order processing bottleneck causing shipping delays, inventory discrepancies, and customer complaints.',
    solution: 'Built Make.com workflow connecting Shopify, inventory system, shipping provider, and customer communication channels.',
    platform: 'make',
    results: {
      timeSaved: '25 hours/week',
      costReduction: '$60,000/year',
      efficiency: '95% error reduction'
    },
    image: "https://images.unsplash.com/photo-1648824572507-24e6e9ef6916",
    alt: 'E-commerce fulfillment center with organized shelves and worker using tablet for order processing',
    testimonial: {
      quote: 'Our order processing went from chaos to seamless efficiency. The automation handles everything from inventory checks to customer notifications. Our customer satisfaction scores improved by 28% in just three months.',
      author: 'Michael Chen',
      position: 'Operations Director',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15be4265d-1763301291596.png",
      avatarAlt: 'Asian man with glasses wearing white shirt smiling confidently at camera'
    }
  }];


  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Award" size={16} />
            <span>Success Stories</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-text-primary mb-4">
            Real Results from Real Businesses
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            See how companies like yours have transformed their operations with our workflow automation solutions.
          </p>
        </div>

        <div className="space-y-16">
          {caseStudies.map((study, index) =>
          <div
            key={study.id}
            className={`grid lg:grid-cols-2 gap-12 items-center ${
            index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`
            }>

              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <Image
                  src={study.image}
                  alt={study.alt}
                  className="w-full h-full object-cover" />

                  <div className="absolute top-4 left-4">
                    <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  study.platform === 'n8n' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'}`
                  }>
                      {study.platform === 'n8n' ? 'n8n Platform' : 'Make.com Platform'}
                    </span>
                  </div>
                </div>
              </div>

              <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Building2" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-text-primary">{study.clientName}</h3>
                      <p className="text-sm text-text-secondary">{study.industry}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                      <Icon name="AlertCircle" size={20} className="text-warning" />
                      <span>Challenge</span>
                    </h4>
                    <p className="text-text-secondary">{study.challenge}</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-text-primary mb-2 flex items-center space-x-2">
                      <Icon name="Lightbulb" size={20} className="text-primary" />
                      <span>Solution</span>
                    </h4>
                    <p className="text-text-secondary">{study.solution}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 p-6 bg-card rounded-xl border border-border">
                  <div className="text-center">
                    <Icon name="Clock" size={24} className="text-primary mx-auto mb-2" />
                    <p className="text-2xl font-bold text-text-primary">{study.results.timeSaved}</p>
                    <p className="text-xs text-text-secondary">Time Saved</p>
                  </div>
                  <div className="text-center">
                    <Icon name="DollarSign" size={24} className="text-success mx-auto mb-2" />
                    <p className="text-2xl font-bold text-text-primary">{study.results.costReduction}</p>
                    <p className="text-xs text-text-secondary">Cost Reduction</p>
                  </div>
                  <div className="text-center">
                    <Icon name="TrendingUp" size={24} className="text-accent mx-auto mb-2" />
                    <p className="text-2xl font-bold text-text-primary">{study.results.efficiency}</p>
                    <p className="text-xs text-text-secondary">Efficiency Gain</p>
                  </div>
                </div>

                <div className="bg-primary/5 rounded-xl p-6 border-l-4 border-primary">
                  <div className="flex items-start space-x-4">
                    <Image
                    src={study.testimonial.avatar}
                    alt={study.testimonial.avatarAlt}
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0" />

                    <div>
                      <p className="text-text-secondary italic mb-4">"{study.testimonial.quote}"</p>
                      <div>
                        <p className="font-semibold text-text-primary">{study.testimonial.author}</p>
                        <p className="text-sm text-text-secondary">{study.testimonial.position}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-16 text-center">
          <p className="text-text-secondary mb-6">
            Ready to achieve similar results for your business?
          </p>
          <button className="inline-flex items-center space-x-2 bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-medium transition-colors duration-300">
            <Icon name="Calendar" size={20} />
            <span>Schedule Your Free Automation Audit</span>
          </button>
        </div>
      </div>
    </section>);

};

export default CaseStudySection;